import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useFormikContext } from "formik"
import { debounce, isEmpty } from "lodash"
import { ApiQueryAutocomplete } from "./ApiQueryAutocomplete"
import {
    DynamicAutocompleteInput, FieldInitialValue,
    IConfigMode
} from "../../Modules/Forms/formTypes"
import { useQuery } from "../../Modules/Query/useQuery"
import {
    DeliveryPayloadType,
    IBasketDeliveryPaymentFormValues,
    IBasketMeetingPlanningFormField, IFormikSelectOptionsProps
} from "../../Types/MeetingPlanning/deliveryForm"
import { useToken } from "../../Modules/Login/useToken"
import { useSelector } from "react-redux"
import { selectCurrentEnvironment } from "../../Modules/Environment/envSelectors"
import { selectUser } from "../../Modules/User/userSelectors"
import {
    fetchBillReceivers,
    fetchDimensionDetailsForCompany, fetchResources
} from "../../Modules/MeetingPlanning/Api/MeetingPlanningApi"
import { ResourceType } from "../../Types/MeetingPlanning/userResources"

interface IDynamicAutocompleteProps {
    field: DynamicAutocompleteInput
}

const DEBOUNCE_DELAY_TIME = 2
const DEBOUNCE_SEARCH_WAIT_TIME = 300

export function DynamicAutocomplete({ field }: IDynamicAutocompleteProps) {
    const token = useToken()
    const environment = useSelector(selectCurrentEnvironment)
    const user = useSelector(selectUser)
    const [search, setSearch] = useState("")
    const [debouncedSearch, setDebouncedSearch] = useState("")
    const [open, setOpen] = useState(false)
    const [hasTyped, setHasTyped] = useState(false)
    const [loading, setLoading] = useState(false)
    const [options, setOptions] = useState<Array<{label: string, value: string}>>([])
    const [selectedValue, setSelectedValue] = useState<{label: string, value: string} | null>(null)

    const autocompleteMode: IConfigMode = field?.mode || IConfigMode.LOAD_ON_OPEN
    const isSearchAsYouType = autocompleteMode === IConfigMode.SEARCH_AS_YOU_TYPE
    const isLoadOnOpen = autocompleteMode === IConfigMode.LOAD_ON_OPEN

    const isSearchValid = debouncedSearch.length === 0 || debouncedSearch.length >= DEBOUNCE_DELAY_TIME

    const enabled = (isSearchAsYouType && debouncedSearch.length >= DEBOUNCE_DELAY_TIME) || (isLoadOnOpen && open && isSearchValid)

    const debounceSearchHandler = useMemo(() => debounce((value: string) => setDebouncedSearch(value), DEBOUNCE_SEARCH_WAIT_TIME), [])

    useEffect(() => {
        debounceSearchHandler(search)
        return () => {
            debounceSearchHandler.cancel()
        }
    }, [search, debounceSearchHandler])

    const { values, setFieldValue } = useFormikContext<IBasketDeliveryPaymentFormValues>()

    const dimensionId = field.dimensionId || null

    const { response: deliveryLocationsQuery } = useQuery(
        () => fetchResources(token, user?.id!, environment, ResourceType.ROOM),
        [token, environment],
        Boolean(user?.id && field.searchQuery?.name === 'resources-for-user')
    )

    const { response: billReceiversQuery } = useQuery(
        () => fetchBillReceivers(token, user!.companyId, environment),
        [token, environment, user?.companyId, field.searchQuery?.name],
        Boolean(token && user?.companyId && environment && field.searchQuery?.name === 'bill-receivers-for-company')
    )

    const { response: costCenterQuery } = useQuery(
        () => {
            setLoading(true)
            return fetchDimensionDetailsForCompany(token, environment, {
                companyId: user!.companyId,
                dimensionId: dimensionId!,
                search
            }).finally(() => setLoading(false))
        },
        [token, environment, user!.companyId, search, dimensionId],
        Boolean(token && user?.companyId && environment && dimensionId)
    )

    const deliveryLocationsResponseData = useMemo(() => {
        if (!deliveryLocationsQuery.loading &&
            !deliveryLocationsQuery.failed &&
            deliveryLocationsQuery.response &&
            !isEmpty(deliveryLocationsQuery.response.data)) {
            return deliveryLocationsQuery.response.data.serviceCenters
                .flatMap((center) => center.resources)
                .map((resource) => ({
                    label: resource.displayName,
                    value: `${resource.id}`,
                }))
        }

        return null
    }, [deliveryLocationsQuery])

    const billReceiversResponseData = useMemo(() => {
        if (!billReceiversQuery.loading &&
            !billReceiversQuery.failed &&
            billReceiversQuery.response &&
            !isEmpty(billReceiversQuery.response.data)) {
            return billReceiversQuery.response.data.billReceivers.map((billReceiver) => ({
                label: billReceiver.name,
                value: `${billReceiver.id}`
            }))
        }

        return null
    }, [billReceiversQuery])

    const costCenterResponseData = useMemo(() => {
        if (!costCenterQuery.loading &&
            !costCenterQuery.failed &&
            costCenterQuery.response &&
            !isEmpty(costCenterQuery.response.data)) {
            return costCenterQuery.response.data.accountingDimension.values.map((item) => ({
                label: item.label,
                value: item.value,
            }))
        }

        return null
    }, [costCenterQuery])

    useEffect(() => {
        const value = field.initialValue && Array.isArray(field.initialValue) ? field?.initialValue?.[0]?.value : null

        if (deliveryLocationsResponseData?.length) {
            setOptions(deliveryLocationsResponseData)
            const deliveryLocation = deliveryLocationsResponseData.find((item) => item.value === value)
            if (deliveryLocation) {
                setSelectedValue(deliveryLocation)
                setFieldValue(field.name, deliveryLocation)
            }
        }
        if (billReceiversResponseData?.length) {
            setOptions(billReceiversResponseData)
            const billReceiverValue = billReceiversResponseData.find((item) => item.value === value)
            if (billReceiverValue) {
                setSelectedValue(billReceiverValue)
                setFieldValue(field.name, billReceiverValue)
            }
        }
        if (costCenterResponseData?.length) {
            const currentValue = Array.isArray(field.initialValue) && field.initialValue?.length ? field.initialValue.find((item: IFormikSelectOptionsProps) => item.id === dimensionId) : null

            if (currentValue) {
                const costCenterValue = costCenterResponseData.find((item) => item.value === currentValue.value)
                if (costCenterValue) {
                    setSelectedValue(costCenterValue)
                    setFieldValue(field.name, { id: dimensionId, value: costCenterValue })
                }
            }

            setOptions(costCenterResponseData)
        }
    }, [costCenterResponseData, billReceiversResponseData, deliveryLocationsResponseData, field.key, field.initialValue])

    useEffect(() => {
        if (costCenterResponseData?.length) {
            setFieldValue(field.name, selectedValue ? [{
                ...selectedValue,
                id: +dimensionId!,
            }] : [])

            return
        }

        setFieldValue(field.name, selectedValue ? [selectedValue] : [])
    }, [selectedValue, billReceiversResponseData, costCenterResponseData, deliveryLocationsResponseData, dimensionId, setFieldValue])

    useEffect(() => {
        const deliveriesKeys = ['DELIVERY_TYPE', 'DELIVERY_LOCATIONS']
        if (field.key && deliveriesKeys.includes(field.key)) {
            // @ts-ignore
            if ((values as IBasketMeetingPlanningFormField)?.hasOtherDeliveryLocation || values?.DELIVERY_TYPE !== DeliveryPayloadType.DELIVERY) {
                setFieldValue("DELIVERY_LOCATIONS", [])
                setSelectedValue(null)
            }
        }
        // @ts-ignore
    }, [(values as IBasketMeetingPlanningFormField)?.hasOtherDeliveryLocation, values?.DELIVERY_TYPE, field.key])

    const handleInputChange = (event: React.ChangeEvent<{}>, value: string) => {
        if (isSearchAsYouType || open) {
            setSearch(value)
            setHasTyped(true)
        }
    }

    const handleChange = (event: React.SyntheticEvent, value: { label: string, value: string }) => {
        setSelectedValue(value)
        if (isLoadOnOpen) {
            setSearch("")
            setDebouncedSearch("")
        }
    }

    const handleClose = () => {
        setOpen(false)
        if (!hasTyped && isLoadOnOpen) {
            setSearch("")
            setDebouncedSearch("")
        }
        setHasTyped(false)
    }

    return field?.isActive ? (
        <ApiQueryAutocomplete
            isLoading={enabled && loading}
            required={field.required}
            multiple={false}
            name={field.name}
            isFloatingLabel={false}
            label={field?.label}
            placeholder={`Select ${field?.placeholder}`}
            isError={!costCenterQuery.loading && costCenterQuery.failed}
            options={options}
            getOptionLabel={(option: { label: string, value: string }) => option.label || ""}
            getOptionSelected={(option: { label: string, value: string }) => option.label || ""}
            value={selectedValue}
            onChange={handleChange}
            onInputChange={handleInputChange}
            onOpen={() => setOpen(true)}
            onClose={handleClose}
            size="small"
        />
    ) : <></>
}
